import {useCallback, useEffect, useState} from "react"
import axios from "axios"
import {Alert, Badge, Button, Card, Col, Form, Row, Spinner} from "react-bootstrap"

import {useAuth} from "../../context/AuthProvider"
import {useTranslation} from "react-i18next"

type MailResult = {
    type: string
    language: "de" | "en"
    sent: boolean
    message?: string
}

export default function TestMailManagementComponent() {
    const {t} = useTranslation("admin")
    const auth = useAuth()
    const isAdmin = auth.hasGate("admin")
    const [types, setTypes] = useState<string[]>([])
    const [recipient, setRecipient] = useState("")
    const [loading, setLoading] = useState(true)
    const [busy, setBusy] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [results, setResults] = useState<MailResult[]>([])

    const load = useCallback(async () => {
        if (!isAdmin) return setLoading(false)
        try {
            const response = await axios.get<{types: string[]}>(
                "/api/testmail",
                auth.axiosAuthHeader()
            )
            setTypes(response.data.types)
            setError(null)
        } catch {
            setError(t("testMails.messages.loadError"))
        } finally {
            setLoading(false)
        }
    }, [auth, isAdmin, t])

    useEffect(() => { load() }, [load])

    const isValidRecipient = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipient.trim())

    const send = async (type?: string) => {
        if (!isValidRecipient) return
        const action = type ?? "all"
        const count = type ? 2 : types.length * 2
        if (!window.confirm(t(type
            ? "testMails.confirmTemplate"
            : "testMails.confirmAll", {count, email: recipient.trim()}))) return

        setBusy(action)
        setError(null)
        setResults([])
        try {
            const response = await axios.post<{results: MailResult[]}>(
                `/api/testmail/${action}`,
                {to: recipient.trim()},
                auth.axiosAuthHeader()
            )
            setResults(response.data.results ?? [])
        } catch (error: unknown) {
            setError(axios.isAxiosError(error)
                ? error.response?.data?.message ?? t("testMails.messages.sendError")
                : t("testMails.messages.sendError"))
        } finally {
            setBusy(null)
        }
    }

    if (!isAdmin) return <div className="component"><Alert variant="danger">
        {t("adminOnly")}
    </Alert></div>

    return <div className="component">
        <Card>
            <Card.Header><h2 className="mb-0">
                <i className="bi bi-envelope-paper me-2"/>{t("testMails.title")}
            </h2></Card.Header>
            <Card.Body>
                <p>{t("testMails.description")}</p>
                {error && <Alert variant="danger">{error}</Alert>}
                {results.length > 0 && <Alert variant={results.every(result => result.sent)
                    ? "success"
                    : "warning"}>
                    {t("testMails.messages.result", {
                        sent: results.filter(result => result.sent).length,
                        failed: results.filter(result => !result.sent).length,
                    })}
                    {results.some(result => !result.sent) && <ul className="mb-0 mt-2">
                        {results.filter(result => !result.sent).map(result => <li
                            key={`${result.type}-${result.language}`}>
                            {t(`testMails.templates.${result.type}`)} ({result.language.toUpperCase()}): {result.message}
                        </li>)}
                    </ul>}
                </Alert>}

                <Form.Group className="mb-4" controlId="test-mail-recipient">
                    <Form.Label>{t("testMails.recipient")}</Form.Label>
                    <Form.Control type="email" value={recipient}
                        isInvalid={recipient.length > 0 && !isValidRecipient}
                        placeholder="name@example.org"
                        onChange={event => setRecipient(event.target.value)}/>
                    <Form.Control.Feedback type="invalid">
                        {t("testMails.messages.invalidEmail")}
                    </Form.Control.Feedback>
                </Form.Group>

                {loading ? <div className="text-center"><Spinner/></div> : <>
                    <div className="d-flex justify-content-end mb-3">
                        <Button variant="danger" disabled={busy !== null || !isValidRecipient}
                            onClick={() => send()}>
                            {busy === "all" && <Spinner size="sm" className="me-2"/>}
                            <i className="bi bi-envelope-paper-fill me-1"/>
                            {t("testMails.sendAll", {count: types.length * 2})}
                        </Button>
                    </div>
                    <Row className="g-3">
                        {types.map(type => <Col xs={12} md={6} xl={4} key={type}>
                            <Card className="h-100">
                                <Card.Body className="d-flex flex-column">
                                    <Card.Title>{t(`testMails.templates.${type}`)}</Card.Title>
                                    <div className="mb-3">
                                        <Badge bg="secondary" className="me-1">DE</Badge>
                                        <Badge bg="secondary">EN</Badge>
                                    </div>
                                    <Button className="mt-auto" disabled={busy !== null || !isValidRecipient}
                                        onClick={() => send(type)}>
                                        {busy === type && <Spinner size="sm" className="me-2"/>}
                                        {t("testMails.sendBoth")}
                                    </Button>
                                </Card.Body>
                            </Card>
                        </Col>)}
                    </Row>
                </>}
            </Card.Body>
        </Card>
    </div>
}
